/**
 * Telefone: DDI, máscara e formatação.
 *
 * Portado do bzapper (`apps/admin/src/lib/phone.ts`), que já resolveu isto contra
 * números reais de WhatsApp. O que ele guarda vale repetir:
 *
 * - **O banco recebe `+` seguido só de dígitos** (E.164). A máscara é apresentação,
 *   e apresentação nunca deve vazar para o armazenamento — é o mesmo princípio que
 *   faz `normalizar_telefone` existir no backend: dois registros só convergem no
 *   mesmo nó-ponte do grafo se produzirem exatamente a mesma string.
 * - **Só o Brasil recebe máscara.** Inventar formato para os outros países produz
 *   número errado com aparência de certo.
 */

export interface Pais {
  codigo: string;
  ddi: string;
  nome: string;
  bandeira: string;
}

export const PAISES: Pais[] = [
  { codigo: 'BR', ddi: '+55', nome: 'Brasil', bandeira: '🇧🇷' },
  { codigo: 'US', ddi: '+1', nome: 'EUA / Canadá', bandeira: '🇺🇸' },
  { codigo: 'PT', ddi: '+351', nome: 'Portugal', bandeira: '🇵🇹' },
  { codigo: 'AR', ddi: '+54', nome: 'Argentina', bandeira: '🇦🇷' },
  { codigo: 'UY', ddi: '+598', nome: 'Uruguai', bandeira: '🇺🇾' },
  { codigo: 'PY', ddi: '+595', nome: 'Paraguai', bandeira: '🇵🇾' },
  { codigo: 'CL', ddi: '+56', nome: 'Chile', bandeira: '🇨🇱' },
  { codigo: 'CO', ddi: '+57', nome: 'Colômbia', bandeira: '🇨🇴' },
  { codigo: 'MX', ddi: '+52', nome: 'México', bandeira: '🇲🇽' },
  { codigo: 'ES', ddi: '+34', nome: 'Espanha', bandeira: '🇪🇸' },
  { codigo: 'DE', ddi: '+49', nome: 'Alemanha', bandeira: '🇩🇪' },
  { codigo: 'FR', ddi: '+33', nome: 'França', bandeira: '🇫🇷' },
  { codigo: 'GB', ddi: '+44', nome: 'Reino Unido', bandeira: '🇬🇧' },
  { codigo: 'IT', ddi: '+39', nome: 'Itália', bandeira: '🇮🇹' },
];

export const PAIS_PADRAO = PAISES[0];

export function apenasDigitos(v: string): string {
  return v.replace(/\D/g, '');
}

/** Trata as duas formas: fixo `(11) 9999-9999` e celular `(11) 99999-9999`. */
export function mascaraBR(digitos: string): string {
  const d = digitos.slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/**
 * Completa o nono dígito de **celulares** brasileiros que vieram sem ele.
 *
 * Fixos não recebem o 9. A distinção está no primeiro dígito do assinante: 6–9 é
 * móvel, 2–5 é fixo. Inserir o 9 num fixo produziria um número que não existe —
 * e existe WhatsApp em número residencial.
 */
export function completarNonoDigitoBR(digitos: string): string {
  if (digitos.length === 10 && /[6-9]/.test(digitos[2])) {
    return digitos.slice(0, 2) + '9' + digitos.slice(2);
  }
  return digitos;
}

export function detectarPais(telefone: string): Pais {
  if (!telefone.startsWith('+')) return PAIS_PADRAO;
  // Do DDI mais longo para o mais curto: `+1` casaria antes de `+351`.
  const ordenados = [...PAISES].sort((a, b) => b.ddi.length - a.ddi.length);
  return ordenados.find((p) => telefone.startsWith(p.ddi)) ?? PAIS_PADRAO;
}

export function digitosLocais(telefone: string, pais: Pais): string {
  if (telefone.startsWith(pais.ddi)) return apenasDigitos(telefone.slice(pais.ddi.length));
  return apenasDigitos(telefone);
}

/** `+5551995453808` → `+55 (51) 99545-3808`. Sem máscara fora do Brasil. */
export function formatarTelefone(telefone: string | null | undefined): string {
  if (!telefone) return '';
  const normalizado = telefone.startsWith('+') ? telefone : '+' + apenasDigitos(telefone);
  const pais = detectarPais(normalizado);
  let digitos = digitosLocais(normalizado, pais);
  if (!digitos) return telefone;
  if (pais.codigo === 'BR') digitos = completarNonoDigitoBR(digitos);
  const local = pais.codigo === 'BR' ? mascaraBR(digitos) : digitos;
  return `${pais.ddi} ${local}`;
}
